import type { PasswordPolicy, PasswordPolicyTarget } from '../api/client';
import {
  PASSWORD_POLICY_TARGETS,
  findOverlappingTargets,
} from '../crypto/passwordPolicy';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { showConfirmDialog } from '../ui/confirmDialog';
import { bindModalDismiss, createModalShell } from '../ui/modalDialogShell';

function newPolicyId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `00000000-0000-7000-8000-${Date.now().toString(16).padStart(12, '0').slice(-12)}`;
}

export function createEmptyPasswordPolicy(name = ''): PasswordPolicy {
  return {
    id: newPolicyId(),
    name,
    appliesTo: [],
    minLength: 12,
    maxLength: 256,
    requireUppercase: false,
    requireLowercase: false,
    requireDigit: false,
    requireSpecial: false,
    minCharacterClasses: 0,
    bannedWords: [],
    blockCommonPasswords: false,
    disallowEmailLocalPart: false,
    maxRepeatedChars: null,
    maxSequentialChars: null,
    maxAgeDays: null,
  };
}

export function clonePasswordPolicy(policy: PasswordPolicy): PasswordPolicy {
  return {
    ...policy,
    appliesTo: [...policy.appliesTo],
    bannedWords: [...policy.bannedWords],
  };
}

export function clonePasswordPolicies(policies: PasswordPolicy[]): PasswordPolicy[] {
  return policies.map(clonePasswordPolicy);
}

function claimedTargets(policies: PasswordPolicy[], exceptIndex: number): Set<PasswordPolicyTarget> {
  const claimed = new Set<PasswordPolicyTarget>();
  policies.forEach((policy, index) => {
    if (index === exceptIndex) {
      return;
    }
    for (const target of policy.appliesTo) {
      claimed.add(target);
    }
  });
  return claimed;
}

function optionalInt(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export function validatePasswordPoliciesDraft(policies: PasswordPolicy[]): Record<string, string> {
  const fields: Record<string, string> = {};
  const overlaps = new Set(findOverlappingTargets(policies));
  const seenIds = new Set<string>();

  policies.forEach((policy, index) => {
    const prefix = `passwordPolicies[${index}]`;
    if (!policy.id || seenIds.has(policy.id)) {
      fields[`${prefix}.id`] = 'duplicate_id';
    } else {
      seenIds.add(policy.id);
    }
    if (!policy.name.trim()) {
      fields[`${prefix}.name`] = 'required';
    }
    if (policy.appliesTo.length === 0) {
      fields[`${prefix}.appliesTo`] = 'required';
    }
    for (const target of policy.appliesTo) {
      if (overlaps.has(target)) {
        fields[`${prefix}.appliesTo`] = 'overlapping_target';
      }
    }
    if (policy.minLength < 1 || policy.minLength > 128) {
      fields[`${prefix}.minLength`] = 'out_of_range';
    }
    if (policy.maxLength < policy.minLength || policy.maxLength > 256) {
      fields[`${prefix}.maxLength`] = 'out_of_range';
    }
    if (policy.minCharacterClasses < 0 || policy.minCharacterClasses > 4) {
      fields[`${prefix}.minCharacterClasses`] = 'out_of_range';
    }
    if (policy.maxRepeatedChars != null && (policy.maxRepeatedChars < 1 || policy.maxRepeatedChars > 32)) {
      fields[`${prefix}.maxRepeatedChars`] = 'out_of_range';
    }
    if (policy.maxSequentialChars != null && (policy.maxSequentialChars < 2 || policy.maxSequentialChars > 32)) {
      fields[`${prefix}.maxSequentialChars`] = 'out_of_range';
    }
    if (policy.maxAgeDays != null && (policy.maxAgeDays < 1 || policy.maxAgeDays > 3650)) {
      fields[`${prefix}.maxAgeDays`] = 'out_of_range';
    }
  });

  return fields;
}

function formatAppliesTo(policy: PasswordPolicy): string {
  if (policy.appliesTo.length === 0) {
    return t('admin.passwordPolicies.list.none');
  }
  return policy.appliesTo
    .map((target) => t(`admin.settings.passwordPolicies.targets.${target}`))
    .join(', ');
}

function formatRulesSummary(policy: PasswordPolicy): string {
  const parts = [
    t('admin.passwordPolicies.list.length', {
      min: String(policy.minLength),
      max: String(policy.maxLength),
    }),
  ];
  const classes: string[] = [];
  if (policy.requireUppercase) {
    classes.push(t('admin.passwordPolicies.list.upper'));
  }
  if (policy.requireLowercase) {
    classes.push(t('admin.passwordPolicies.list.lower'));
  }
  if (policy.requireDigit) {
    classes.push(t('admin.passwordPolicies.list.digit'));
  }
  if (policy.requireSpecial) {
    classes.push(t('admin.passwordPolicies.list.special'));
  }
  if (classes.length > 0) {
    parts.push(classes.join(' · '));
  }
  if (policy.minCharacterClasses > 0) {
    parts.push(t('admin.passwordPolicies.list.classes', { min: String(policy.minCharacterClasses) }));
  }
  if (policy.blockCommonPasswords) {
    parts.push(t('admin.passwordPolicies.list.common'));
  }
  if (policy.maxAgeDays != null) {
    parts.push(t('admin.passwordPolicies.list.maxAge', { days: String(policy.maxAgeDays) }));
  }
  return parts.join(' · ');
}

function readPolicyFromForm(form: HTMLFormElement, base: PasswordPolicy): PasswordPolicy {
  const data = new FormData(form);
  const appliesTo = [...form.querySelectorAll<HTMLInputElement>('input[name="appliesTo"]:checked')]
    .map((input) => input.value as PasswordPolicyTarget);

  return {
    ...base,
    name: String(data.get('name') ?? '').trim(),
    appliesTo,
    minLength: Number.parseInt(String(data.get('minLength') ?? ''), 10) || base.minLength,
    maxLength: Number.parseInt(String(data.get('maxLength') ?? ''), 10) || base.maxLength,
    requireUppercase: data.get('requireUppercase') === 'on',
    requireLowercase: data.get('requireLowercase') === 'on',
    requireDigit: data.get('requireDigit') === 'on',
    requireSpecial: data.get('requireSpecial') === 'on',
    minCharacterClasses: Number.parseInt(String(data.get('minCharacterClasses') ?? ''), 10) || 0,
    blockCommonPasswords: data.get('blockCommonPasswords') === 'on',
    disallowEmailLocalPart: data.get('disallowEmailLocalPart') === 'on',
    maxRepeatedChars: optionalInt(String(data.get('maxRepeatedChars') ?? '')),
    maxSequentialChars: optionalInt(String(data.get('maxSequentialChars') ?? '')),
    maxAgeDays: optionalInt(String(data.get('maxAgeDays') ?? '')),
    bannedWords: String(data.get('bannedWords') ?? '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean),
  };
}

function validateSinglePolicy(
  policy: PasswordPolicy,
  allPolicies: PasswordPolicy[],
  editIndex: number | null,
): string | null {
  const draft = clonePasswordPolicies(allPolicies);
  if (editIndex == null) {
    draft.push(policy);
  } else {
    draft[editIndex] = policy;
  }
  const fields = validatePasswordPoliciesDraft(draft);
  const values = Object.values(fields);
  if (values.includes('overlapping_target')) {
    return t('admin.settings.passwordPolicies.errors.overlapping');
  }
  if (values.includes('required')) {
    return t('admin.passwordPolicies.dialog.required');
  }
  if (values.length > 0) {
    return t('admin.settings.errors.validation_failed');
  }
  return null;
}

function openPasswordPolicyDialog(input: {
  policy: PasswordPolicy;
  policies: PasswordPolicy[];
  editIndex: number | null;
}): Promise<PasswordPolicy | null> {
  return new Promise((resolve) => {
    const draft = clonePasswordPolicy(input.policy);
    const claimed = claimedTargets(input.policies, input.editIndex ?? -1);
    const isNew = input.editIndex == null;

    const shell = createModalShell({
      titleKey: isNew ? 'admin.passwordPolicies.dialog.createTitle' : 'admin.passwordPolicies.dialog.editTitle',
      titleId: 'admin-password-policy-dialog-title',
      className: 'admin-password-policy-dialog',
      helpTopic: 'admin-password-policies',
    });

    const form = document.createElement('form');
    form.className = 'form-grid admin-password-policy-dialog__form';
    form.id = 'admin-password-policy-dialog-form';

    const appliesToHtml = PASSWORD_POLICY_TARGETS.map((target) => {
      const checked = draft.appliesTo.includes(target) ? 'checked' : '';
      const disabled = !draft.appliesTo.includes(target) && claimed.has(target) ? 'disabled' : '';
      return `
        <label class="checkbox-row">
          <input type="checkbox" name="appliesTo" value="${target}" ${checked} ${disabled} />
          <span data-i18n="admin.settings.passwordPolicies.targets.${target}"></span>
        </label>`;
    }).join('');

    form.innerHTML = `
      <label class="admin-password-policy-dialog__name">
        <span data-i18n="admin.passwordPolicies.dialog.name"></span>
        <input type="text" name="name" required maxlength="120" value="${escapeAttr(draft.name)}" />
      </label>
      <fieldset class="admin-password-policy-dialog__group admin-password-policy-dialog__targets">
        <legend data-i18n="admin.settings.passwordPolicies.appliesTo"></legend>
        <div class="admin-password-policy-dialog__targets-grid">${appliesToHtml}</div>
      </fieldset>
      <div class="admin-password-policy-dialog__columns">
        <fieldset class="admin-password-policy-dialog__group">
          <legend data-i18n="admin.passwordPolicies.dialog.groups.composition"></legend>
          <div class="admin-password-policy-dialog__fields">
            <label>
              <span data-i18n="admin.settings.passwordPolicies.minLength"></span>
              <input type="number" name="minLength" class="input-compact" min="1" max="128" required value="${draft.minLength}" />
            </label>
            <label>
              <span data-i18n="admin.settings.passwordPolicies.maxLength"></span>
              <input type="number" name="maxLength" class="input-compact" min="1" max="256" required value="${draft.maxLength}" />
            </label>
            <label>
              <span data-i18n="admin.settings.passwordPolicies.minCharacterClasses"></span>
              <input type="number" name="minCharacterClasses" class="input-compact" min="0" max="4" value="${draft.minCharacterClasses}" />
            </label>
          </div>
          <div class="admin-password-policy-dialog__checks">
            <label class="checkbox-row">
              <input type="checkbox" name="requireUppercase" ${draft.requireUppercase ? 'checked' : ''} />
              <span data-i18n="admin.settings.passwordPolicies.requireUppercase"></span>
            </label>
            <label class="checkbox-row">
              <input type="checkbox" name="requireLowercase" ${draft.requireLowercase ? 'checked' : ''} />
              <span data-i18n="admin.settings.passwordPolicies.requireLowercase"></span>
            </label>
            <label class="checkbox-row">
              <input type="checkbox" name="requireDigit" ${draft.requireDigit ? 'checked' : ''} />
              <span data-i18n="admin.settings.passwordPolicies.requireDigit"></span>
            </label>
            <label class="checkbox-row">
              <input type="checkbox" name="requireSpecial" ${draft.requireSpecial ? 'checked' : ''} />
              <span data-i18n="admin.settings.passwordPolicies.requireSpecial"></span>
            </label>
          </div>
        </fieldset>
        <fieldset class="admin-password-policy-dialog__group">
          <legend data-i18n="admin.passwordPolicies.dialog.groups.limits"></legend>
          <div class="admin-password-policy-dialog__fields">
            <label>
              <span data-i18n="admin.settings.passwordPolicies.maxRepeatedChars"></span>
              <input type="number" name="maxRepeatedChars" class="input-compact" min="1" max="32" value="${draft.maxRepeatedChars ?? ''}" placeholder="—" />
            </label>
            <label>
              <span data-i18n="admin.settings.passwordPolicies.maxSequentialChars"></span>
              <input type="number" name="maxSequentialChars" class="input-compact" min="2" max="32" value="${draft.maxSequentialChars ?? ''}" placeholder="—" />
            </label>
            <label>
              <span data-i18n="admin.settings.passwordPolicies.maxAgeDays"></span>
              <input type="number" name="maxAgeDays" class="input-compact" min="1" max="3650" value="${draft.maxAgeDays ?? ''}" placeholder="—" />
            </label>
          </div>
          <div class="admin-password-policy-dialog__checks">
            <label class="checkbox-row">
              <input type="checkbox" name="blockCommonPasswords" ${draft.blockCommonPasswords ? 'checked' : ''} />
              <span data-i18n="admin.settings.passwordPolicies.blockCommonPasswords"></span>
            </label>
            <label class="checkbox-row">
              <input type="checkbox" name="disallowEmailLocalPart" ${draft.disallowEmailLocalPart ? 'checked' : ''} />
              <span data-i18n="admin.settings.passwordPolicies.disallowEmailLocalPart"></span>
            </label>
          </div>
          <label class="admin-password-policy-dialog__banned">
            <span data-i18n="admin.settings.passwordPolicies.bannedWords"></span>
            <textarea name="bannedWords" rows="3">${escapeText(draft.bannedWords.join('\n'))}</textarea>
          </label>
        </fieldset>
      </div>
      <p class="error-text admin-password-policy-dialog__error" id="admin-password-policy-dialog-error" hidden></p>
    `;

    shell.body.append(form);

    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.className = 'button ghost';
    cancelButton.innerHTML = '<span data-i18n="common.cancel"></span>';

    const saveButton = document.createElement('button');
    saveButton.type = 'submit';
    saveButton.className = 'button primary';
    saveButton.setAttribute('form', 'admin-password-policy-dialog-form');
    saveButton.innerHTML = `<span data-i18n="${isNew ? 'admin.passwordPolicies.dialog.create' : 'admin.passwordPolicies.dialog.save'}"></span>`;

    shell.actions.append(cancelButton, saveButton);
    document.body.append(shell.backdrop);
    applyI18n(shell.dialog);

    const errorEl = form.querySelector('#admin-password-policy-dialog-error') as HTMLElement;
    const nameInput = form.querySelector('input[name="name"]') as HTMLInputElement;

    const finish = (result: PasswordPolicy | null) => {
      unbindLocale();
      unbindDismiss();
      shell.backdrop.remove();
      resolve(result);
    };

    const unbindLocale = bindLocaleRefresh(() => applyI18n(shell.dialog));
    const unbindDismiss = bindModalDismiss({
      backdrop: shell.backdrop,
      closeButton: shell.closeButton,
      onClose: () => finish(null),
    });

    cancelButton.addEventListener('click', () => finish(null));

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      errorEl.hidden = true;
      if (!form.reportValidity()) {
        return;
      }
      const next = readPolicyFromForm(form, draft);
      const validationError = validateSinglePolicy(next, input.policies, input.editIndex);
      if (validationError) {
        errorEl.hidden = false;
        errorEl.textContent = validationError;
        return;
      }
      finish(next);
    });

    nameInput.focus();
    nameInput.select();
  });
}

async function addPolicy(
  policies: PasswordPolicy[],
  onChange: (next: PasswordPolicy[]) => void,
  render: () => void,
): Promise<void> {
  const created = await openPasswordPolicyDialog({
    policy: createEmptyPasswordPolicy(t('admin.settings.passwordPolicies.newName')),
    policies,
    editIndex: null,
  });
  if (!created) {
    return;
  }
  policies.push(created);
  onChange(clonePasswordPolicies(policies));
  render();
}

export function mountPasswordPoliciesEditor(
  container: HTMLElement,
  policies: PasswordPolicy[],
  onChange: (next: PasswordPolicy[]) => void,
  options?: { addButton?: HTMLButtonElement | null },
): void {
  const addButton = options?.addButton ?? null;

  const render = () => {
    container.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'admin-password-policies';

    if (policies.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'muted';
      empty.setAttribute('data-i18n', 'admin.passwordPolicies.list.empty');
      wrap.append(empty);
    } else {
      const tableWrap = document.createElement('div');
      tableWrap.className = 'admin-password-policies__table-wrap';
      const table = document.createElement('table');
      table.className = 'data-table admin-password-policies__table';
      table.innerHTML = `
        <thead>
          <tr>
            <th data-i18n="admin.passwordPolicies.list.name"></th>
            <th data-i18n="admin.passwordPolicies.list.appliesTo"></th>
            <th data-i18n="admin.passwordPolicies.list.rules"></th>
            <th class="data-table__actions-heading" data-i18n="admin.passwordPolicies.list.actions"></th>
          </tr>
        </thead>
        <tbody></tbody>
      `;
      const tbody = table.querySelector('tbody')!;
      policies.forEach((policy, index) => {
        const row = document.createElement('tr');
        row.dataset.policyIndex = String(index);
        row.innerHTML = `
          <td data-label="${escapeAttr(t('admin.passwordPolicies.list.name'))}">${escapeText(policy.name || t('admin.settings.passwordPolicies.newName'))}</td>
          <td data-label="${escapeAttr(t('admin.passwordPolicies.list.appliesTo'))}">${escapeText(formatAppliesTo(policy))}</td>
          <td data-label="${escapeAttr(t('admin.passwordPolicies.list.rules'))}">${escapeText(formatRulesSummary(policy))}</td>
          <td class="data-table__actions">
            <div class="data-table__actions-inner">
              <button type="button" class="button ghost" data-action="edit" data-i18n="admin.passwordPolicies.list.edit"></button>
              <button type="button" class="button ghost" data-action="delete" data-i18n="admin.settings.passwordPolicies.delete"></button>
            </div>
          </td>
        `;
        tbody.append(row);
      });
      tableWrap.append(table);
      wrap.append(tableWrap);
    }

    container.append(wrap);
    applyI18n(container);

    wrap.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }
      const actionEl = target.closest('[data-action]') as HTMLElement | null;
      if (!actionEl) {
        return;
      }
      const action = actionEl.dataset.action;
      const row = actionEl.closest('[data-policy-index]') as HTMLElement | null;
      const index = Number(row?.dataset.policyIndex ?? -1);
      if (index < 0 || !policies[index]) {
        return;
      }

      if (action === 'edit') {
        void (async () => {
          const updated = await openPasswordPolicyDialog({
            policy: policies[index]!,
            policies,
            editIndex: index,
          });
          if (!updated) {
            return;
          }
          policies[index] = updated;
          onChange(clonePasswordPolicies(policies));
          render();
        })();
        return;
      }

      if (action === 'delete') {
        void (async () => {
          const confirmed = await showConfirmDialog({
            titleKey: 'admin.passwordPolicies.deleteConfirm.title',
            messageKey: 'admin.passwordPolicies.deleteConfirm.message',
            messageParams: { name: policies[index]!.name },
            confirmLabelKey: 'admin.settings.passwordPolicies.delete',
            confirmClassName: 'button danger',
          });
          if (!confirmed) {
            return;
          }
          policies.splice(index, 1);
          onChange(clonePasswordPolicies(policies));
          render();
        })();
      }
    });
  };

  if (addButton) {
    addButton.onclick = () => {
      void addPolicy(policies, onChange, render);
    };
  }

  render();
}

function escapeAttr(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

function escapeText(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}
